import React, { useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { glassStyle } from './themes';

/**
 * The questions that decide whether someone signs up — ownership, lock-in,
 * money, and what happens when they outgrow the defaults.
 *
 * Answers are written to be true today rather than aspirational. Where the
 * honest answer is a limitation (Stripe is the only processor; the NFT side
 * is EVM-only) it says so: the alternative is someone discovering it after
 * they have moved their domain over.
 */
const QA = [
  {
    q: 'Do I keep my domain and my customers?',
    a: 'Yes. The domain stays registered to you at your own registrar — you point a record at us and can point it elsewhere whenever you like. Customers, orders and products are your data and can be exported.',
  },
  {
    q: 'Who receives the money from a sale?',
    a: 'You do, once you connect your own Stripe account — payments go straight to it and we are not in the path. Before that you can run the whole checkout on the platform account with Stripe test cards, so you can try it end to end without signing up for anything.',
  },
  {
    q: 'Do I need to know anything technical?',
    a: 'Only for the domain, and only once: you add one record at your registrar and we show you exactly what to paste. Everything else — pages, products, tax, email — is forms and a page builder.',
  },
  {
    q: 'What does it cost?',
    a: 'Creating a brand, building pages and connecting a domain are free, and there is no card required to start. Paid plans for the heavier features are not published yet — when they are, nothing you have already built stops working.',
  },
  {
    q: 'Can I run more than one brand?',
    a: 'Yes, from a single login. Each brand has its own domain, catalogue, storefront and team, and you switch between them from the top bar. People you invite only see the brands you add them to.',
  },
  {
    q: 'Can I invite my team?',
    a: 'Yes. Invite by email and scope each person to the sections they need — support, fulfilment or content — rather than handing over the whole account. There is an activity log of who changed what.',
  },
  {
    q: 'What about the NFT side — do I need it?',
    a: 'No. It sits alongside the rest of the brand and can be ignored entirely if you are just selling products. If you do use it, it is EVM chains today; collections and drops are managed in the same dashboard as everything else.',
  },
  {
    q: 'What happens to my site if I leave?',
    a: 'Point your DNS back at wherever you are going and the domain follows you immediately — nothing is held hostage while a cancellation is processed. Export your catalogue, customers and orders before you go.',
  },
];

export default function FAQ({ t }) {
  const [open, setOpen] = useState(0);

  return (
    <div id='faq' style={{ maxWidth: 820, margin: '0 auto', padding: '80px 24px', position: 'relative', zIndex: 1 }}>
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, color: t.text, letterSpacing: '-1px', margin: '0 0 14px' }}>
          Questions worth asking first
        </h2>
        <p style={{ color: t.textSub, fontSize: 18, margin: 0 }}>
          Including the ones with awkward answers.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {QA.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={item.q} style={{ ...glassStyle(t), overflow: 'hidden' }}>
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 16, padding: '18px 22px', background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', color: t.text, fontSize: 16, fontWeight: 600,
                  fontFamily: 'inherit', lineHeight: 1.45,
                }}
              >
                {item.q}
                <MdExpandMore
                  size={22}
                  style={{
                    flexShrink: 0, color: t.textMuted,
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.22s ease',
                  }}
                />
              </button>
              {isOpen && (
                <p style={{ color: t.textSub, fontSize: 15, lineHeight: 1.75, margin: 0, padding: '0 22px 20px' }}>
                  {item.a}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
